import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap'
import {
  useAdminSection,
  useCreateAdminSection,
  useUpdateAdminSection,
  useDeleteAdminSection
} from 'api/hooks/use-admin-sections'

const SectionForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: section, isLoading } = useAdminSection({ id })
  const createMutation = useCreateAdminSection({
    options: { onSuccess: () => navigate('/admin/sections') }
  })
  const updateMutation = useUpdateAdminSection({
    id,
    options: { onSuccess: () => navigate('/admin/sections') }
  })
  const deleteMutation = useDeleteAdminSection({
    options: { onSuccess: () => navigate('/admin/sections') }
  })

  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    display_key: '',
    display_key_two: '',
    date_field: '',
    amount_field: '',
    primary_date_column_id: '',
    primary_amount_column_id: '',
    folder: '',
    unit: '',
    parent_module: ''
  })

  useEffect(() => {
    if (section) {
      setFormData({
        name: section.name || '',
        abbreviation: section.abbreviation || '',
        display_key: section.display_key || '',
        display_key_two: section.display_key_two || '',
        date_field: section.date_field || '',
        amount_field: section.amount_field || '',
        primary_date_column_id: section.primary_date_column_id || '',
        primary_amount_column_id: section.primary_amount_column_id || '',
        folder: section.folder || '',
        unit: section.unit || '',
        parent_module: section.parent_module || ''
      })
    }
  }, [section])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Offer only what the model would accept: date columns, and measures.
  const sectionColumns = section?.table_columns || []
  const dateColumns = sectionColumns.filter((col) => col.display_as === 'date')
  const measureColumnsForSection = sectionColumns.filter((col) => col.role === 'measure')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this module? This action cannot be undone.')) {
      deleteMutation.mutate(id)
    }
  }

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Module' : 'Create Module'}</h2>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message || 'An error occurred'}
        </Alert>
      )}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Abbreviation *</Form.Label>
                  <Form.Control type="text" name="abbreviation" value={formData.abbreviation} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Key</Form.Label>
                  <Form.Control type="text" name="display_key" value={formData.display_key} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Key Two</Form.Label>
                  <Form.Control type="text" name="display_key_two" value={formData.display_key_two} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date Field (label)</Form.Label>
                  <Form.Control type="text" name="date_field" value={formData.date_field} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount Field (label)</Form.Label>
                  <Form.Control type="text" name="amount_field" value={formData.amount_field} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Control type="text" name="unit" value={formData.unit} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            {/* Chart defaults when a request names no date or amount; a new module has no columns
                until its first save. */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Date Column</Form.Label>
                  <Form.Select
                    name="primary_date_column_id"
                    value={formData.primary_date_column_id}
                    onChange={handleChange}
                    disabled={!section}
                  >
                    <option value="">{section ? 'None' : 'Save first, then choose'}</option>
                    {dateColumns.map((col) => (
                      <option key={col.id} value={col.id}>{col.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Amount Column</Form.Label>
                  <Form.Select
                    name="primary_amount_column_id"
                    value={formData.primary_amount_column_id}
                    onChange={handleChange}
                    disabled={!section}
                  >
                    <option value="">{section ? 'None' : 'Save first, then choose'}</option>
                    {measureColumnsForSection.map((col) => (
                      <option key={col.id} value={col.id}>{col.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Folder</Form.Label>
                  <Form.Control type="text" name="folder" value={formData.folder} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent Module</Form.Label>
                  <Form.Control type="text" name="parent_module" value={formData.parent_module} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="light-primary" onClick={() => navigate('/admin/sections')} disabled={isSubmitting}>
                  Cancel
                </Button>
              </div>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting}>
                  Delete Module
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default SectionForm
